"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import base from "lib/base";
import styles from "styles/components/hero.module.css";

const HERO_AUTOPLAY_MS = 5000;
const BEST_SELLER_FALLBACK_BG = "/assets/img/background.jpg";

const resolveBannerImage = (banner) => {
  const raw = String(banner?.banner || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${String(base.cdnUrl || "").replace(/\/+$/, "")}/${raw.replace(/^\/+/, "")}`;
};

const resolvePath = (path) => {
  const raw = String(path || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${String(base.cdnUrl || "").replace(/\/+$/, "")}/${raw.replace(/^\/+/, "")}`;
};

const resolveBestSellerBookImage = (path) => {
  const raw = String(path || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const root = String(base.cdnUrl || "").replace(/\/+$/, "");
  const segment = raw.replace(/^\/+/, "");
  return `${root}/450/${segment}`;
};

const Hero = ({ banners = [], bestSellers = [] }) => {
  const slides = Array.isArray(banners) ? banners.filter(Boolean) : [];
  const topBooks = Array.isArray(bestSellers)
    ? bestSellers.filter(Boolean)
    : [];
  const [heroProgressKey, setHeroProgressKey] = useState(0);
  /** Swiper SSR/client DOM өөр байдаг тул hydration хүртэл ижил статик markup */
  const [swiperMounted, setSwiperMounted] = useState(false);

  useEffect(() => {
    setSwiperMounted(true);
  }, []);

  const handleHeroSlideChange = useCallback((swiper) => {
    setHeroProgressKey(swiper.realIndex);
  }, []);

  const renderSafeTitle = (title) => {
    if (!title) return "";
    const parts = String(title)
      .split(/<br\s*\/?>/i)
      .filter(Boolean);
    if (parts.length <= 1) return String(title);
    return parts.map((part, index) => (
      <span key={`${part}-${index}`}>
        {part}
        {index < parts.length - 1 ? <br /> : null}
      </span>
    ));
  };

  return (
    <section className={`section ${styles.hero}`}>
      <div className="container">
        <div className={`row ${styles.wrapper}`}>
          <div className={`col-12 col-lg-8 ${styles.left}`}>
            <div className={styles.carouselWrap}>
              {slides.length > 1 ? (
                <div className={styles.autoplayProgress} aria-hidden>
                  <div
                    key={heroProgressKey}
                    className={styles.autoplayProgressFill}
                    style={{
                      animationDuration: `${HERO_AUTOPLAY_MS}ms`,
                    }}
                  />
                </div>
              ) : null}
              <Swiper
                modules={[Autoplay, Pagination]}
                autoplay={{
                  delay: HERO_AUTOPLAY_MS,
                  disableOnInteraction: false,
                }}
                onSlideChange={handleHeroSlideChange}
                pagination={{ clickable: true }}
                loop={slides.length > 1}
                className={styles.carousel}
              >
                {slides.map((banner, index) => {
                  const type = banner?.type || "text";
                  const imageUrl = resolveBannerImage(banner);
                  const hitText = banner?.hitText || "";
                  const title = banner?.name || "";
                  const details = banner?.details || "";
                  const link = banner?.link || "";

                  return (
                    <SwiperSlide key={banner?._id || imageUrl || title}>
                      <div
                        className={`${styles.slide} ${
                          type === "fillImage" ? styles.fillImage : ""
                        }`}
                      >
                        {type === "fillImage" ? (
                          <>
                            {imageUrl ? (
                              <div
                                className={styles.fillMedia}
                                style={{ backgroundImage: `url(${imageUrl})` }}
                                aria-hidden
                              />
                            ) : null}
                            <div className={styles.fillScrim} aria-hidden />
                            <div
                              className={`${styles.contentBox} ${styles.fillContent}`}
                            >
                              <div className={styles.textBlock}>
                                {hitText ? (
                                  <span className={styles.hit}>{hitText}</span>
                                ) : null}
                                {title ? (
                                  <h2 className={styles.title}>
                                    {renderSafeTitle(title)}
                                  </h2>
                                ) : null}
                                {details && (
                                  <p className={styles.details}>{details}</p>
                                )}
                                {link && (
                                  <Link href={link} className={styles.cta}>
                                    Худалдан авах <ArrowRight size={16} />
                                  </Link>
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className={styles.contentBox}>
                            <div className={styles.split}>
                              <div className={styles.textBlock}>
                                {hitText ? (
                                  <span className={styles.hit}>{hitText}</span>
                                ) : null}
                                {title ? (
                                  <h2 className={styles.title}>
                                    {renderSafeTitle(title)}
                                  </h2>
                                ) : null}
                                {details && (
                                  <p className={`${styles.details} ${styles.detailsText}`}>
                                    {details}
                                  </p>
                                )}
                                {link && (
                                  <Link href={link} className={styles.cta}>
                                    Худалдан авах <ArrowRight size={16} />
                                  </Link>
                                )}
                              </div>
                              <div className={styles.imageBlock}>
                                {imageUrl && (
                                  <img
                                    src={imageUrl}
                                    alt={title || "Banner"}
                                    className={styles.image}
                                    width={500}
                                    height={300}
                                    loading={index === 0 ? "eager" : "lazy"}
                                    decoding="async"
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>
          </div>
          <div className={`col-12 col-lg-4 ${styles.right}`}>
            {topBooks.length > 0 ? (
              <Swiper
                modules={[Autoplay, Pagination]}
                autoplay={{ delay: 4000, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                loop={topBooks.length > 1}
                className={styles.bestSellerSwiper}
                style={{ height: "100%", width: "100%" }}
              >
                {topBooks.map((book, bookIndex) => {
                  const rawBookImg =
                    book?.pictures?.[0] || book?.images?.[0] || book?.image;
                  const bookImg = resolveBestSellerBookImage(rawBookImg);
                  const rawCover = book?.cover;
                  const coverImg = resolvePath(rawCover);
                  const bestSellerBgUrl =
                    coverImg && String(coverImg).trim()
                      ? coverImg
                      : BEST_SELLER_FALLBACK_BG;

                  let authorName = "";
                  if (book?.authors && book.authors.length > 0) {
                    const firstAuthor = book.authors[0];
                    if (
                      typeof firstAuthor === "object" &&
                      firstAuthor !== null
                    ) {
                      authorName =
                        firstAuthor.nickname || firstAuthor.name || "";
                    } else if (typeof firstAuthor === "string") {
                      authorName = firstAuthor;
                    }
                  }

                  return (
                    <SwiperSlide key={book._id}>
                      <div
                        className={styles.bestSeller}
                        style={{
                          position: "relative",
                          backgroundImage: `url(${bestSellerBgUrl})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      >
                        <div className={styles.bestSellerBody}>
                          <span className={styles.bestLabel}>
                            Шилдэг борлуулалт
                          </span>
                          <div className={styles.bestImageWrap}>
                            {bookImg && (
                              <img
                                src={bookImg}
                                alt=""
                                className={styles.bestImage}
                                width={200}
                                height={300}
                                loading={bookIndex === 0 ? "eager" : "lazy"}
                                decoding="async"
                              />
                            )}
                          </div>
                          {authorName ? (
                            <p className={styles.bestAuthor}>{authorName}</p>
                          ) : null}
                          <h3 className={styles.bestTitle}>{book.name}</h3>
                        </div>
                        <Link
                          href={book?.slug ? `/books/${book.slug}` : "/books"}
                          className={styles.bestSellerLink}
                          aria-label={`${book.name} — дэлгэрэнгүй`}
                        />
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            ) : (
              <div
                className={styles.bestSeller}
                style={{
                  backgroundImage: `url(${BEST_SELLER_FALLBACK_BG})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div className={styles.bestSellerBody}>
                  <span className={styles.bestLabel}>Шилдэг борлуулалт</span>
                  <div className={styles.bestImageWrap}>
                    <img
                      src="/media/app/best-seller.png"
                      alt=""
                      className={styles.bestImage}
                      width={200}
                      height={300}
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                  <h3 className={styles.bestTitle}>Р.Ч. Спрол</h3>
                </div>
                <Link
                  href="/books"
                  className={styles.bestSellerLink}
                  aria-label="Номууд — Шилдэг борлуулалт"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
